import { useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { ChartContainer } from "@/components/ui/chart";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getRecentAppActivity } from "@/utils/appAnalytics";

const AppActivityChart = () => {
  const [timeframe, setTimeframe] = useState("30d");
  
  const getTimeframeDays = (timeframe: string) => {
    switch (timeframe) {
      case "7d": return 7;
      case "30d": return 30;
      case "90d": return 90;
      default: return 30;
    }
  };

  const activityData = getRecentAppActivity(getTimeframeDays(timeframe));

  const chartConfig = {
    installations: {
      label: "Installations",
      color: "hsl(142, 76%, 36%)", // Green
    },
    updates: {
      label: "Updates", 
      color: "hsl(217, 91%, 60%)", // Blue
    },
    removals: {
      label: "Removals",
      color: "hsl(346, 87%, 43%)", // Red
    },
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const date = new Date(label).toLocaleDateString();
      return (
        <div className="bg-background border rounded-lg shadow-md p-3">
          <p className="font-medium mb-2">{date}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.dataKey}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const formatXAxisLabel = (tickItem: string) => {
    const date = new Date(tickItem);
    if (timeframe === "7d") {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else if (timeframe === "30d") {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <GlassCard variant="strong" hover={false}>
      <GlassCardHeader>
        <div className="flex items-center justify-between">
          <GlassCardTitle className="flex items-center gap-2">
            App Activity Timeline
            <span className="text-sm font-normal text-muted-foreground">
              Recent deployments
            </span>
          </GlassCardTitle>
          <Tabs value={timeframe} onValueChange={setTimeframe} className="w-auto">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="7d">7d</TabsTrigger>
              <TabsTrigger value="30d">30d</TabsTrigger>
              <TabsTrigger value="90d">90d</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </GlassCardHeader>
      <GlassCardContent>
        <ChartContainer config={chartConfig} className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={activityData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="date"
                tickFormatter={formatXAxisLabel}
                className="fill-muted-foreground"
              />
              <YAxis className="fill-muted-foreground" />
              <Tooltip content={CustomTooltip} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="installations" 
                stroke="hsl(142, 76%, 36%)"
                strokeWidth={2}
                dot={{ r: 2 }}
                name="Installations"
              />
              <Line 
                type="monotone" 
                dataKey="updates" 
                stroke="hsl(217, 91%, 60%)"
                strokeWidth={2}
                dot={{ r: 2 }}
                name="Updates"
              />
              <Line 
                type="monotone" 
                dataKey="removals" 
                stroke="hsl(346, 87%, 43%)"
                strokeWidth={2}
                dot={{ r: 2 }}
                name="Removals"
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </GlassCardContent>
    </GlassCard>
  );
};

export default AppActivityChart;